import { ComponentType } from 'react';

/**
 * Modal manager.
 *
 * Modals are kept in a module-level store and rendered by a single
 * `<ModalOutlet />` mounted inside the app tree (see `modal-outlet.tsx`).
 *
 * Previously `openModal` spun up a separate React root (`createRoot`) on a
 * detached `<body>` element. That isolated each modal in its own React tree
 * with none of the app's context providers (Router, Mixpanel, styled-components
 * `StyleSheetManager`, …) and a separate Radix layer tree, which broke
 * dropdowns rendered inside those modals (no hover, no scroll, the modal
 * closing on interaction, freezes). Rendering through the shared store keeps the
 * same imperative API while mounting modals in the app's own root.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ModalComponent = ComponentType<any>;

export type ModalEntry = {
  id: number;
  Modal: ModalComponent;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any;
};

let modals: ModalEntry[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

export const subscribeModals = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getModalsSnapshot = (): ModalEntry[] => modals;

export const closeModal = (id: number) => {
  const next = modals.filter((modal) => modal.id !== id);
  if (next.length === modals.length) {
    return;
  }
  modals = next;
  notify();
};

/**
 * Open a modal. Returns a function that closes it.
 * Signature is unchanged from the previous implementation.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const openModal = (Modal: ModalComponent, options: any) => {
  const id = nextId++;
  modals = [...modals, { id, Modal, options }];
  notify();
  return () => closeModal(id);
};
